import React, { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import MessageList from '@/components/Room/MessageList'
import ChatPill from '@/components/Student/BottomRow/ChatPill'
import AdvisorRow from '@/components/Student/BottomRow/AdvisorRow'
import { setInput } from '@/redux/slices/chatSlice'
import { StudentDecision } from '@/redux/slices/studentDecisionWebsocketSlice'
import {
  adjudicateDecision,
  approveDecision,
} from '@/redux/asyncThunks/studentDecisionWebsocket'
import { SendAdjudicationModal } from '@/components/Student/SendAdjudicationModal'
import AdjudicationContext from '@/components/Room/AdjudicationContext'
import Navbar from '@/components/Layout/Navbar'
import ProfessorAdjudication from '@/components/Student/ProfessorAdjudication'
import DecisionSection from '@/components/Room/DecisionSection'
import { useGamePhase } from '@/hooks/useGamePhase'
import { RootState } from '@/redux/store'
import { useSelector } from 'react-redux'
import { ActiveTab, isAIPlayerTab, isCharterTab } from '@/constants/chat'
import { CharterPrepBody } from '@/features/charter/components/Shell/CharterPrepBody'
import { CharterGroupBody } from '@/features/charter/components/Shell/CharterGroupBody'
import { shallowEqual } from 'react-redux'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { UserRole } from '@/constants/room'
import PlayerUsageSummaryCard from '@/components/Student/PlayerUsageSummaryCard'
import { useParams } from 'react-router-dom'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TimerMode } from '@/components/settings/timer-configuration/types'

type ChatRoomProps = {
  showAdvisors?: boolean
  formatMessage?: (content: string, selectedStudent?: string) => React.ReactNode
  showActionButtons?: boolean
  showNavbar?: boolean
  showTabbar?: boolean
  showChatPill?: boolean
  showNegotiationPill?: boolean
  showAdvisorPill?: boolean
  showNegotiation?: boolean
  showDecisions?: boolean
  phaseId?: number | null
  isWhiteCell?: boolean
}

const ChatRoom: React.FC<ChatRoomProps> = React.memo(
  ({
    showAdvisors = true,
    showActionButtons = false,
    showNavbar = true,
    showTabbar = true,
    showNegotiationPill = true,
    showAdvisorPill = true,
    showDecisions = false,
    phaseId = null,
    isWhiteCell,
  }) => {
    const dispatch = useAppDispatch()
    const { activeTab, advisorDraft } = useAppSelector(
      (state) => state.chat,
      shallowEqual
    )
    const negotiationDraft = useAppSelector(
      (state) => state.chat.negotiationDrafts[activeTab] || ''
    )
    const { decisions } = useAppSelector(
      (state) => state.decisionsWS,
      shallowEqual
    )
    const {
      isRandRPhase,
      isAdvisorPhase,
      isNegotiationPhase,
      isDecisionPhase,
      manualMode,
      currentRound,
    } = useGamePhase()
    const charterSession = useAppSelector((state) => state.charter.session)
    const { gameEnded } = useSelector(
      (state: RootState) => state.waitingRoomWS,
      shallowEqual
    )
    const { user } = useEffectiveUser()
    const { roomId = '' } = useParams()
    const isProfessor = user?.role === UserRole.Professor
    const isResearchMode =
      useAppSelector((state) => state.room.currentRoom?.game_mode) ===
      TimerMode.RESEARCH_MODE
    const [selectedDecision, setSelectedDecision] =
      useState<StudentDecision | null>(null)
    const [selectedStudentDecisions, setSelectedStudentDecisions] = useState<
      StudentDecision[]
    >([])

    const handleSetSelectedDecision = useCallback(
      (decision: StudentDecision, studentDecisions: StudentDecision[]) => {
        setSelectedDecision(decision)
        setSelectedStudentDecisions(studentDecisions)
      },
      []
    )

    useEffect(() => {
      if (activeTab === ActiveTab.TeamRoom) {
        // No phase restrictions for Team Room
        return
      }
      if (isAIPlayerTab(activeTab)) {
        return
      }

      if (!isRandRPhase || manualMode) {
        if (activeTab === ActiveTab.Advisor) {
          dispatch(setInput(advisorDraft))
        } else {
          dispatch(setInput(negotiationDraft))
        }
      }
    }, [
      activeTab,
      isRandRPhase,
      manualMode,
      advisorDraft,
      negotiationDraft,
      dispatch,
    ])

    const canRenderChatInterface =
      isWhiteCell ||
      isNegotiationPhase ||
      isRandRPhase ||
      isAdvisorPhase ||
      isDecisionPhase ||
      gameEnded

    // Charter channel tabs render their own chat body. This must short-circuit
    // ABOVE the `canRenderChatInterface` gate, since charter is shown pre-game
    // (when no game phase is active and the "Waiting for the Game" gate hides
    // the normal chat interface).
    if (isCharterTab(activeTab)) {
      const isGameStarted = currentRound > 0
      const isCharterLocked = charterSession?.status === 'locked'
      return (
        <>
          {showNavbar && (
            <div className='ms-6'>
              <Navbar />
            </div>
          )}
          <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
            {activeTab === ActiveTab.CharterPrep ? (
              <CharterPrepBody
                readOnly={
                  isGameStarted ||
                  isCharterLocked ||
                  charterSession?.phase !== 'phase1_prep' ||
                  charterSession?.round_status === 'completed'
                }
              />
            ) : (
              <CharterGroupBody
                roomId={roomId}
                readOnly={
                  isGameStarted ||
                  isCharterLocked ||
                  charterSession?.round_status === 'completed'
                }
              />
            )}
          </div>
        </>
      )
    }

    return (
      <>
        {showNavbar && (
          <div className='ms-6'>
            <Navbar />
          </div>
        )}
        <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
          {showTabbar ? (
            <div
              className={`flex flex-col h-full min-h-0 ${showNavbar ? 'py-9' : 'py-4'}`}
            >
              {canRenderChatInterface ? (
                <>
                  <div className='flex flex-col flex-1 min-h-0 ml-[2.5rem] px-14'>
                    {activeTab === ActiveTab.Summary ? (
                      <PlayerUsageSummaryCard roomId={roomId} />
                    ) : (
                      <>
                        <MessageList />
                        {showDecisions && <DecisionSection phaseId={phaseId} />}
                      </>
                    )}
                  </div>

                  <div className='ml-[2.5rem] px-14 pt-2'>
                    {activeTab === ActiveTab.Advisor &&
                      isRandRPhase &&
                      !manualMode &&
                      !isProfessor && (
                        <Alert className='mb-3 text-amber-900 bg-amber-50 border-amber-300'>
                          <AlertDescription className='font-medium text-amber-800'>
                            Note: Event Analyst Advisor is only available for the
                            Orientation & Context phase.
                          </AlertDescription>
                        </Alert>
                      )}
                    {activeTab === ActiveTab.Advisor &&
                      isAdvisorPhase &&
                      !manualMode &&
                      !isProfessor && (
                        <Alert className='mb-3 text-amber-900 bg-amber-50 border-amber-300'>
                          <AlertDescription className='font-medium text-amber-800'>
                            Note: Advisors are only available for the Strategic
                            Preparation phase.
                          </AlertDescription>
                        </Alert>
                      )}
                    {showAdvisors && activeTab !== ActiveTab.Summary && (
                      <AdvisorRow />
                    )}
                    {activeTab === ActiveTab.Advisor
                      ? showAdvisorPill && (
                          <ChatPill showActionButtons={showActionButtons} />
                        )
                      : activeTab === ActiveTab.Summary
                        ? null
                        : activeTab === ActiveTab.TeamRoom
                          ? showNegotiationPill && (
                              <ChatPill showActionButtons={showActionButtons} />
                            )
                          : isAIPlayerTab(activeTab)
                            ? showNegotiationPill &&
                              !isProfessor && (
                                <ChatPill showActionButtons={showActionButtons} />
                              )
                            : showNegotiationPill && (
                                <ChatPill
                                  showActionButtons={showActionButtons}
                                />
                              )}
                  </div>
                </>
              ) : (
                <div className='flex flex-col justify-center items-center w-full h-full gap-4'>
                  {isWhiteCell && <AdjudicationContext />}
                  <h1 className='text-2xl font-bold leading-8 text-dark-blue text-center'>
                    Waiting for the Game to Start
                  </h1>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`flex-1 min-w-0 overflow-x-hidden ml-[2.5rem] px-14 ${
                showNavbar ? 'pt-9' : 'pt-4'
              }`}
            >
              {isWhiteCell && (
                <div className='mb-4'>
                  <AdjudicationContext />
                </div>
              )}
              <SendAdjudicationModal
                selectedDecision={selectedDecision}
                studentDecisions={selectedStudentDecisions}
                roomId={roomId}
                isResearchMode={isResearchMode}
                onClose={() => {
                  setSelectedDecision(null)
                  setSelectedStudentDecisions([])
                }}
                onSend={(adjudication, decisionId) => {
                  if (adjudication && decisionId) {
                    dispatch(
                      adjudicateDecision({
                        adjudication: adjudication,
                        decisionId: decisionId,
                      })
                    )
                    setSelectedDecision(null)
                    setSelectedStudentDecisions([])
                  }
                }}
                onSave={(decisionId, approvalStatus, formattedDecision) => {
                  dispatch(
                    approveDecision({
                      approvalStatus,
                      decisionId,
                      formattedDecision,
                    })
                  )
                }}
              />

              <ProfessorAdjudication
                roomId={roomId}
                decisions={decisions}
                setSelectedDecision={handleSetSelectedDecision}
              />
            </div>
          )}
        </div>
      </>
    )
  }
)

export default ChatRoom
